import { supabase } from "@/integrations/supabase/client";

export interface PolymarketEvent {
  id: string;
  event_id: string;
  slug: string | null;
  title: string;
  subtitle: string | null;
  description: string | null;
  image: string | null;
  icon: string | null;
  category: string | null;
  subcategory: string | null;
  volume: number;
  volume_24hr: number;
  liquidity: number;
  is_active: boolean;
  is_closed: boolean;
  is_featured: boolean;
  start_date: string | null;
  end_date: string | null;
  markets_count: number;
  outcomes: Array<{
    question: string;
    outcomes: Array<{ name: string; price: number }>;
  }>;
  created_at: string;
  updated_at: string;
  is_bookmarked?: boolean;
}

export interface PriceAlert {
  id: string;
  user_id: string;
  event_id: string;
  market_question: string;
  outcome_name: string;
  alert_type: "above" | "below";
  target_price: number;
  current_price_at_creation: number;
  is_active: boolean;
  triggered_at: string | null;
  created_at: string;
}

export const polymarketEventService = {
  async syncEvents() {
    const { data, error } = await supabase.functions.invoke("sync-polymarket-events");
    if (error) throw error;
    return data;
  },

  async getEvents(filters?: {
    isActive?: boolean;
    category?: string;
    cryptoFinanceOnly?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
    sortBy?: "volume" | "volume_24hr" | "end_date" | "created_at";
    sortOrder?: "asc" | "desc";
  }) {
    let query = supabase
      .from("polymarket_events")
      .select("*", { count: "exact" });

    if (filters?.isActive !== undefined) {
      query = query.eq("is_active", filters.isActive);
    }

    // Filter to crypto/finance only
    if (filters?.cryptoFinanceOnly) {
      query = query.eq("is_crypto_finance", true);
    }

    if (filters?.category) {
      query = query.eq("category", filters.category);
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    // Default sort by volume
    const sortColumn = filters?.sortBy || "volume";
    const sortAscending = filters?.sortOrder === "asc";
    query = query.order(sortColumn, { ascending: sortAscending, nullsFirst: false });

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    
    const events = (data || []).map(event => ({
      ...event,
      outcomes: (event.outcomes as PolymarketEvent['outcomes']) || []
    })) as PolymarketEvent[];
    
    return { events, count: count || 0 };
  },

  async getEventById(id: string) {
    // Try by UUID first, then by event_id
    let { data, error } = await supabase
      .from("polymarket_events")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (!data) {
      const result = await supabase
        .from("polymarket_events")
        .select("*")
        .eq("event_id", id)
        .maybeSingle();
      data = result.data;
      error = result.error;
    }

    if (error) throw error;
    if (!data) throw new Error("Event not found");
    
    return {
      ...data,
      outcomes: (data.outcomes as PolymarketEvent['outcomes']) || []
    } as PolymarketEvent;
  },

  async getCategories() {
    const { data, error } = await supabase
      .from("polymarket_events")
      .select("category")
      .eq("is_active", true)
      .not("category", "is", null);

    if (error) throw error;

    const categories = [...new Set(data.map((d) => d.category).filter(Boolean))];
    return categories as string[];
  },

  async getUserBookmarks(userId: string) {
    const { data, error } = await supabase
      .from("user_polymarket_bookmarks")
      .select("event_id")
      .eq("user_id", userId);

    if (error) throw error;
    return data.map((d) => d.event_id);
  },

  async bookmarkEvent(userId: string, eventId: string) {
    const { error } = await supabase
      .from("user_polymarket_bookmarks")
      .insert({ user_id: userId, event_id: eventId });

    if (error) throw error;
  },

  async unbookmarkEvent(userId: string, eventId: string) {
    const { error } = await supabase
      .from("user_polymarket_bookmarks")
      .delete()
      .eq("user_id", userId)
      .eq("event_id", eventId);

    if (error) throw error;
  },

  async getBookmarkedEvents(userId: string) {
    const { data: bookmarks, error: bookmarksError } = await supabase
      .from("user_polymarket_bookmarks")
      .select("event_id")
      .eq("user_id", userId);

    if (bookmarksError) throw bookmarksError;

    if (!bookmarks.length) return [];

    const eventIds = bookmarks.map((b) => b.event_id);
    
    const { data: events, error: eventsError } = await supabase
      .from("polymarket_events")
      .select("*")
      .in("event_id", eventIds);

    if (eventsError) throw eventsError;

    return (events || []).map(event => ({
      ...event,
      outcomes: (event.outcomes as PolymarketEvent['outcomes']) || [],
      is_bookmarked: true
    })) as PolymarketEvent[];
  },

  async getRelatedEvents(keywords: string[], limit: number = 3) {
    if (!keywords.length) return [];

    // Crypto-specific terms that should prioritize crypto events
    const cryptoTerms = [
      'bitcoin', 'btc', 'ethereum', 'eth', 'crypto', 'solana', 'sol',
      'xrp', 'ripple', 'dogecoin', 'doge', 'token', 'blockchain', 'defi',
      'nft', 'altcoin', 'binance', 'bnb', 'cardano', 'ada', 'polygon', 'matic',
      'avalanche', 'avax', 'chainlink', 'link', 'litecoin', 'ltc', 'stablecoin',
      'usdt', 'usdc', 'tether', 'coinbase', 'kraken', 'exchange', 'halving',
      'mining', 'staking', 'wallet', 'ledger', 'memecoin', 'shiba', 'pepe'
    ];
    
    // Check if any keywords are crypto-related
    const cryptoKeywords = keywords.filter(kw => 
      cryptoTerms.includes(kw.toLowerCase())
    );
    const hasCryptoKeyword = cryptoKeywords.length > 0;

    let query = supabase
      .from("polymarket_events")
      .select("*")
      .eq("is_active", true);

    if (hasCryptoKeyword) {
      // For crypto articles, ONLY match crypto-related keywords
      const orConditions = cryptoKeywords
        .map(kw => `title.ilike.%${kw}%,description.ilike.%${kw}%`)
        .join(',');
      query = query.or(orConditions);
    } else {
      // For non-crypto articles, use all keywords
      const keywordsToUse = keywords.slice(0, 5);
      const orConditions = keywordsToUse
        .map(kw => `title.ilike.%${kw}%,description.ilike.%${kw}%`)
        .join(',');
      query = query.or(orConditions);
    }

    const { data, error } = await query
      .order("volume", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching related events:", error);
      return [];
    }

    return (data || []).map(event => ({
      ...event,
      outcomes: (event.outcomes as PolymarketEvent['outcomes']) || []
    })) as PolymarketEvent[];
  },

  // Semantic matching - get events matched by AI
  async getSemanticMatches(articleId: string) {
    const { data, error } = await supabase
      .from("article_event_matches")
      .select(`
        event_id,
        similarity_score,
        matched_keywords
      `)
      .eq("article_id", articleId)
      .order("similarity_score", { ascending: false })
      .limit(3);

    if (error) {
      console.error("Error fetching semantic matches:", error);
      return [];
    }

    if (!data.length) return [];

    // Fetch the actual events
    const eventIds = data.map(d => d.event_id);
    const { data: events, error: eventsError } = await supabase
      .from("polymarket_events")
      .select("*")
      .in("event_id", eventIds);

    if (eventsError) {
      console.error("Error fetching matched events:", eventsError);
      return [];
    }

    // Combine with match data
    return (events || []).map(event => {
      const matchData = data.find(d => d.event_id === event.event_id);
      return {
        ...event,
        outcomes: (event.outcomes as PolymarketEvent['outcomes']) || [],
        similarity_score: matchData?.similarity_score,
        matched_keywords: matchData?.matched_keywords,
      };
    }) as (PolymarketEvent & { similarity_score?: number; matched_keywords?: string[] })[];
  },

  // Trigger semantic matching for an article
  async matchArticleToEvents(articleId: string) {
    const { matchArticleToEvents } = await import("@/lib/eventMatching.functions");
    return await matchArticleToEvents({ data: { article_id: articleId } });
  },

  // Price Alerts
  async getUserAlerts(userId: string) {
    const { data, error } = await supabase
      .from("polymarket_price_alerts")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as PriceAlert[];
  },

  async createAlert(
    userId: string,
    alertData: {
      event_id: string;
      market_question: string;
      outcome_name: string;
      alert_type: "above" | "below";
      target_price: number;
      current_price_at_creation: number;
    }
  ) {
    const { error } = await supabase
      .from("polymarket_price_alerts")
      .insert({
        user_id: userId,
        ...alertData,
      });

    if (error) throw error;
  },

  async deleteAlert(alertId: string) {
    const { error } = await supabase
      .from("polymarket_price_alerts")
      .delete()
      .eq("id", alertId);

    if (error) throw error;
  },

  async deactivateAlert(alertId: string) {
    const { error } = await supabase
      .from("polymarket_price_alerts")
      .update({ is_active: false })
      .eq("id", alertId);

    if (error) throw error;
  },
};
